import { LoaderCircle } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';

import { ScrollArea, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@column/ui';

import { useNavigate } from '~/lib/navigation';
import {
  EventRepository,
  ParsedEvent,
  getEventACHType,
  getEventRoute,
  getEventStatus,
  getEventSummary,
  getRawEventDirection,
  getRawEventType,
  parseEvent,
} from '~/repositories';
import { useSessionStore } from '~/stores/Session';
import { getTimePassed } from '~/util';

interface EventsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EventsSheet: React.FC<EventsSheetProps> = ({ open, onOpenChange }) => {
  const [events, setEvents] = useState<ParsedEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();
  const isLoadingRef = useRef(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const hasMoreRef = useRef(hasMore);
  const eventsRef = useRef(events);

  // Keep refs in sync with state
  hasMoreRef.current = hasMore;
  eventsRef.current = events;

  const fetchEvents = useCallback(async (startingAfter?: string) => {
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    setIsLoading(true);

    try {
      const response = await EventRepository.getAll({ startingAfter, limit: 25 });
      if (response.events.length) {
        const newEvents = response.events.map((event) => parseEvent(event));
        setEvents((prev) => (startingAfter === undefined ? newEvents : prev.concat(newEvents)));
        setHasMore(response.hasMore);
      } else {
        setHasMore(false);
      }
    } catch {
      // Silently fail
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  // Handle infinite scroll via viewport scroll events
  useEffect(() => {
    if (!open) return;

    let viewport: HTMLElement | null = null;

    const handleScroll = () => {
      if (viewport && viewport.offsetHeight + viewport.scrollTop >= viewport.scrollHeight - 100) {
        if (hasMoreRef.current && !isLoadingRef.current && eventsRef.current.length > 0) {
          fetchEvents(eventsRef.current.at(-1)?.id);
        }
      }
    };

    // Small delay to ensure the sheet is fully mounted
    const timeoutId = setTimeout(() => {
      const scrollArea = scrollAreaRef.current;
      if (!scrollArea) return;

      viewport = scrollArea.querySelector("[data-slot='scroll-area-viewport']") as HTMLElement | null;
      if (!viewport) return;

      viewport.addEventListener('scroll', handleScroll, { passive: true });
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (viewport) {
        viewport.removeEventListener('scroll', handleScroll);
      }
    };
  }, [open, fetchEvents]);

  // Subscribe to session changes to refetch events
  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => [state.isSandbox, state.currentUser?.defaultPlatformId],
        (state) => {
          if (typeof state[1] === 'undefined') {
            return;
          }
          if (open) {
            setEvents([]);
            setHasMore(true);
            fetchEvents();
          }
        },
        {
          equalityFn: shallow,
          fireImmediately: false,
        }
      ),
    [open, fetchEvents]
  );

  // Fetch events when sheet opens
  useEffect(() => {
    if (open) {
      setEvents([]);
      setHasMore(true);
      fetchEvents();
    }
  }, [open, fetchEvents]);

  const handleEventClick = (event: ParsedEvent) => {
    const rawType = getRawEventType(event.eventType);
    if (rawType === 'Identity') {
      navigate(
        `${getEventRoute(event.eventType)}/${(event.data.subType ?? 'undefined').toLowerCase()}/${event.data.id}`
      );
    } else if (rawType !== undefined) {
      navigate(`${getEventRoute(event.eventType)}/${event.data.id}`);
    }
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[400px] sm:max-w-[400px] p-0 flex flex-col gap-0 !border-l-0 shadow-[-8px_0_24px_rgba(0,0,0,0.12)]"
      >
        <SheetHeader className="px-5 py-4 border-b border-gray-200 dark:border-white/10">
          <SheetTitle className="text-base font-medium">Events</SheetTitle>
          <SheetDescription className="sr-only">Recent platform events</SheetDescription>
        </SheetHeader>

        <ScrollArea ref={scrollAreaRef} fadeEdges className="flex-1 min-h-0 !rounded-none">
          <ul className="p-2">
            {events.map((event, index) => (
              <li
                key={`event-${event.id}-${index}`}
                className="group relative after:pointer-events-none after:absolute after:inset-x-3 after:bottom-0 after:h-px after:bg-gray-200 dark:after:bg-white/10 last:after:hidden"
              >
                <button
                  type="button"
                  onClick={() => handleEventClick(event)}
                  className="relative z-10 w-full text-left px-3 py-2 rounded-lg bg-transparent transition-all duration-150 ease-out hover:bg-gray-50 dark:hover:bg-white/[0.06] cursor-pointer before:absolute before:inset-x-3 before:-top-px before:h-px before:bg-background before:opacity-0 before:transition-opacity before:duration-150 hover:before:opacity-100 after:absolute after:inset-x-3 after:-bottom-px after:h-px after:bg-background after:opacity-0 after:transition-opacity after:duration-150 hover:after:opacity-100"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground capitalize">
                        {getRawEventDirection(event.eventType)} {getRawEventType(event.eventType)}{' '}
                        {getEventACHType(event.data)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{getEventSummary(event.data)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-xs">{getEventStatus(event.eventType)}</span>
                      <span className="text-xs text-muted-foreground">{getTimePassed(new Date(event.createdAt))}</span>
                    </div>
                  </div>
                </button>
              </li>
            ))}
            {isLoading && (
              <li className="px-3 py-4 text-sm text-muted-foreground flex items-center gap-2">
                <LoaderCircle className="size-4 animate-spin" />
                Loading...
              </li>
            )}
            {!isLoading && events.length === 0 && (
              <li className="px-3 py-8 text-sm text-muted-foreground text-center">No events yet</li>
            )}
          </ul>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
